/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { Table } from "@tanstack/react-table";
import { Cross2Icon } from "@radix-ui/react-icons";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { DataTableViewOptions } from "./data-table-view-options";
import { useUserFilterActions } from "@/pages/users/ducks/filter/hooks/actions";
import { brazilianStates } from "@/data/brazilianStates";
import { useGetNcmOptionsQuery } from "@/services/ncm";

interface DataRow {
  id: number;
  ncm: string;
  state: string;
  iva: string;
  internal_tax_rate: string;
  interstate_tax: string;
  is_active: boolean;
  created_at: Date;
  updated_at: Date;
}

interface DataTableToolbarProps<TData extends DataRow> {
  table: Table<TData>;
}

export function DataTableToolbar<TData extends DataRow>({
  table,
}: DataTableToolbarProps<TData>) {
  const { updateFilter } = useUserFilterActions();
  const [stateValue, setStateValue] = useState<string>("");
  const [statusValue, setStatusValue] = useState<string>("");
  const [ncmValue, setNcmValue] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");

  const { data: ncmOptions } = useGetNcmOptionsQuery();

  const handleSelectChange = (value: string, filterKey: string) => {
    const filterValue = value === "all" ? "" : value;
    updateFilter({ key: filterKey, value: filterValue });

    if (filterKey === "state") {
      setStateValue(value);
    } else if (filterKey === "is_active") {
      setStatusValue(value);
    } else if (filterKey === "ncm") {
      setNcmValue(value);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    updateFilter({ key: "search", value });
  };

  const handleClearFilters = () => {
    updateFilter({ key: "state", value: "" });
    updateFilter({ key: "is_active", value: "" });
    updateFilter({ key: "ncm", value: "" });
    updateFilter({ key: "search", value: "" });
    setStateValue("");
    setStatusValue("");
    setNcmValue("");
    setSearchValue("");
  };

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-end space-x-4">
        <div className="flex flex-col">
          <label
            htmlFor="search"
            className="text-sm font-medium text-gray-700"
          >
            Buscar por NCM
          </label>
          <Input
            id="search"
            placeholder="Digite o número do NCM"
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-8 w-[150px] lg:w-[200px]"
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="state"
            className="text-sm font-medium text-gray-700"
          >
            Filtrar por Estado
          </label>
          <Select
            value={stateValue}
            onValueChange={(value) => handleSelectChange(value, "state")}
          >
            <SelectTrigger
              id="state"
              className="h-8 w-[150px] lg:w-[200px]"
            >
              <SelectValue placeholder="Selecione um estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {brazilianStates.map((state) => (
                <SelectItem key={state.value} value={state.value}>
                  {state.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="is_active"
            className="text-sm font-medium text-gray-700"
          >
            Filtrar por Status
          </label>
          <Select
            value={statusValue}
            onValueChange={(value) => handleSelectChange(value, "is_active")}
          >
            <SelectTrigger
              id="is_active"
              className="h-8 w-[150px] lg:w-[200px]"
            >
              <SelectValue placeholder="Selecione um status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="true">Ativo</SelectItem>
              <SelectItem value="false">Inativo</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="ncm"
            className="text-sm font-medium text-gray-700"
          >
            Filtrar por NCM
          </label>
          <Select
            value={ncmValue}
            onValueChange={(value) => handleSelectChange(value, "ncm")}
          >
            <SelectTrigger
              id="ncm"
              className="h-8 w-[150px] lg:w-[200px]"
            >
              <SelectValue placeholder="Selecione um NCM" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {ncmOptions?.map((ncm) => (
                <SelectItem key={ncm.value} value={ncm.value}>
                  {ncm.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="ghost"
          onClick={handleClearFilters}
          className="h-8 w-[120px] lg:w-[100px] bg-transparent hover:bg-black/10"
        >
          Limpar
          <Cross2Icon className="ml-2 h-4 w-4" />
        </Button>
      </div>

      <DataTableViewOptions table={table} />
    </div>
  );
}
